<template>
  <div class="balance-sheet-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <h4 class="mb-0">Balance Sheet</h4>
      <div>
        <b-button variant="primary" @click="print" size="sm">
          <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
          Print
        </b-button>
        <b-button
          variant="outline-secondary"
          @click="close"
          size="sm"
          class="ml-2"
        >
          <feather-icon icon="XIcon" size="16" class="mr-50" />
          Close
        </b-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem" />
      <p class="mt-3 font-weight-medium">{{ loadingText }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h5 class="text-danger font-weight-medium">{{ error }}</h5>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="page-header">
        <h3 class="school-name">{{ organizationName }}</h3>
        <h5 class="report-title">{{ reportTitle }}</h5>
        <div class="header-meta mt-2">
          <span v-if="asOfDate"><strong>As of:</strong> {{ asOfDate }}</span>
          <span class="separator">|</span>
          <span v-if="fiscalYearTitle"
            ><strong>Fiscal Year:</strong> {{ fiscalYearTitle }}</span
          >
          <span class="separator">|</span>
          <span><strong>Generated:</strong> {{ currentDateTime }}</span>
        </div>
      </div>

      <!-- Balance Status Banner -->
      <div
        v-if="summary"
        class="balance-status-banner"
        :class="summary.isBalanced ? 'balanced' : 'unbalanced'"
      >
        <feather-icon
          v-if="summary.isBalanced"
          icon="CheckCircleIcon"
          size="20"
          class="mr-50"
        />
        <feather-icon v-else icon="AlertCircleIcon" size="20" class="mr-50" />
        {{
          summary.balanceCheckMessage ||
          (summary.isBalanced
            ? "✓ Assets = Liabilities + Equity (BALANCED)"
            : "✗ Balance check failed")
        }}
      </div>

      <!-- Balance Sheet: Left (Assets) | Right (Liabilities & Equity) -->
      <div class="balance-sheet-layout">
        <!-- Left: ASSETS -->
        <div class="bs-column bs-assets">
          <h6 class="bs-section-title">ASSETS</h6>
          <table class="bs-table">
            <tbody>
              <tr
                v-for="(item, idx) in assetsRows"
                :key="'a-' + idx"
                :class="{
                  'bs-total-row': item.isSubtotal,
                  'bs-section-header': item.isSection,
                }"
              >
                <td class="bs-label" :class="{ 'bs-indent': item.indent }">
                  {{ item.label }}
                </td>
                <td class="bs-amount text-right">
                  {{ item.amount != null ? formatCurrency(item.amount) : "" }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="bs-total-block">
            <strong>Total Assets</strong>
            <strong>{{ formatCurrency(totalAssets) }}</strong>
          </div>
        </div>

        <!-- Right: LIABILITIES & EQUITY -->
        <div class="bs-column bs-liabilities">
          <h6 class="bs-section-title">LIABILITIES & EQUITY</h6>
          <table class="bs-table">
            <tbody>
              <tr
                v-for="(item, idx) in liabilitiesEquityRows"
                :key="'le-' + idx"
                :class="{
                  'bs-total-row': item.isSubtotal,
                  'bs-section-header': item.isSection,
                }"
              >
                <td class="bs-label" :class="{ 'bs-indent': item.indent }">
                  {{ item.label }}
                </td>
                <td class="bs-amount text-right">
                  {{ item.amount != null ? formatCurrency(item.amount) : "" }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="bs-total-block">
            <strong>Total Liabilities & Equity</strong>
            <strong>{{ formatCurrency(totalLiabilitiesEquity) }}</strong>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small><strong>Generated:</strong> {{ currentDateTime }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import axios from "axios";
export default {
  name: "BalanceSheetPrint",
  components: {
    BButton,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      loadingText: "Loading balance sheet...",
      error: null,
      rawData: null,
      fiscalYearTitle: "",
      asOfDate: "",
      reportTitle: "Balance Sheet",
      organizationName: "Organization",
      labelMap: {
        cashAndCashEquivalents: "Cash and Cash Equivalents",
        accountsReceivable: "Accounts Receivable",
        inventory: "Inventory",
        prepaidExpenses: "Prepaid Expenses",
        otherCurrentAssets: "Other Current Assets",
        propertyPlantEquipmentGross: "Property, Plant & Equipment (Gross)",
        lessAccumulatedDepreciation: "Less: Accumulated Depreciation",
        propertyPlantEquipmentNet: "Property, Plant & Equipment (Net)",
        intangibleAssets: "Intangible Assets",
        goodwill: "Goodwill",
        accountsPayable: "Accounts Payable",
        shortTermDebt: "Short Term Debt",
        accruedLiabilities: "Accrued Liabilities",
        incomeTaxPayable: "Income Tax Payable",
        longTermDebt: "Long Term Debt",
        deferredTaxLiabilities: "Deferred Tax Liabilities",
        pensionObligations: "Pension Obligations",
        commonStock: "Common Stock",
        additionalPaidInCapital: "Additional Paid-in Capital",
        retainedEarnings: "Retained Earnings",
        lessTreasuryStock: "Less: Treasury Stock",
      },
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    summary() {
      return this.rawData?.summary || null;
    },
    assetsRows() {
      return this.buildAssetsRows();
    },
    liabilitiesEquityRows() {
      return this.buildLiabilitiesEquityRows();
    },
    totalAssets() {
      const s = this.rawData?.summary;
      if (s && (s.totalAssets !== undefined || s.totalAssets === 0))
        return s.totalAssets;
      const a = this.rawData?.assets;
      if (a && (a.totalAssets !== undefined || a.totalAssets === 0))
        return a.totalAssets;
      return 0;
    },
    totalLiabilitiesEquity() {
      const s = this.rawData?.summary;
      if (
        s &&
        (s.totalLiabilitiesAndEquity !== undefined ||
          s.totalLiabilitiesAndEquity === 0)
      )
        return s.totalLiabilitiesAndEquity;
      const le = this.rawData?.liabilitiesAndEquity;
      if (
        le &&
        (le.totalLiabilitiesAndEquity !== undefined ||
          le.totalLiabilitiesAndEquity === 0)
      )
        return le.totalLiabilitiesAndEquity;
      return 0;
    },
  },
  created() {
    this.loadReportData();
  },
  methods: {
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        // API hit commented out - dummy data only for now
        // const { db, campusID, fiscalYearID } = this.$route.query
        // const domain = this.$store.state.domain || 'https://apiv26.myskool.app/api/'
        // const url = `${domain.replace(/\/$/, '')}/BalanceSheet/GetBalanceSheet`
        // const response = await axios.get(url, {
        //   params: { db, campusID, fiscalYearID },
        //   headers: { Authorization: `Bearer ${this.$store.state.userData?.token}`, accept: 'application/json' }
        // })
        // let data = response?.data?.status === 'success' ? response.data.data : response?.data?.data || response?.data
        // if (data && this.isEmptyBalanceSheet(data)) data = this.getDummyBalanceSheetData()
        // else if (!data) data = this.getDummyBalanceSheetData()
        // this.rawData = data
        // this.fiscalYearTitle = data?.fiscalYear || data?.fiscalYearTitle || ''
        // this.asOfDate = data?.asOfDate || ''
        // this.reportTitle = data?.reportTitle || 'Balance Sheet'
        // this.organizationName = data?.companyName || data?.organizationName || this.organizationName

        const dummy = this.getDummyBalanceSheetData();
        this.rawData = dummy;
        this.fiscalYearTitle = dummy.fiscalYear;
        this.asOfDate = dummy.asOfDate;
        this.reportTitle = dummy.reportTitle;
        this.organizationName = dummy.companyName || this.organizationName;
      } catch (err) {
        console.error("Failed to load balance sheet:", err);
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },

    isEmptyBalanceSheet(data) {
      if (!data || !data.summary) return true;
      const s = data.summary;
      const totalA = s.totalAssets ?? data.assets?.totalAssets ?? 0;
      const totalLE =
        s.totalLiabilitiesAndEquity ??
        data.liabilitiesAndEquity?.totalLiabilitiesAndEquity ??
        0;
      return totalA === 0 && totalLE === 0;
    },

    getDummyBalanceSheetData() {
      // Balanced dummy data: Total Assets = Total Liabilities & Equity = 565,000
      return {
        companyName: "MySkool",
        reportTitle: "Balance Sheet",
        asOfDate: new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        fiscalYear:
          new Date().getFullYear() + "-" + (new Date().getFullYear() + 1),
        summary: {
          totalAssets: 565000,
          totalCurrentAssets: 290000,
          totalNonCurrentAssets: 275000,
          totalLiabilities: 295000,
          totalCurrentLiabilities: 100000,
          totalLongTermLiabilities: 195000,
          totalShareholdersEquity: 270000,
          totalLiabilitiesAndEquity: 565000,
          isBalanced: true,
          balanceCheckMessage: "✓ Assets = Liabilities + Equity (BALANCED)",
          difference: 0,
        },
        assets: {
          currentAssets: {
            cashAndCashEquivalents: 150000,
            accountsReceivable: 75000,
            inventory: 50000,
            prepaidExpenses: 10000,
            otherCurrentAssets: 5000,
            total: 290000,
          },
          nonCurrentAssets: {
            propertyPlantEquipmentGross: 200000,
            lessAccumulatedDepreciation: -50000,
            propertyPlantEquipmentNet: 150000,
            intangibleAssets: 25000,
            goodwill: 50000,
            total: 275000,
          },
          totalAssets: 565000,
        },
        liabilitiesAndEquity: {
          currentLiabilities: {
            accountsPayable: 45000,
            shortTermDebt: 30000,
            accruedLiabilities: 15000,
            incomeTaxPayable: 10000,
            total: 100000,
          },
          longTermLiabilities: {
            longTermDebt: 150000,
            deferredTaxLiabilities: 20000,
            pensionObligations: 25000,
            total: 195000,
          },
          totalLiabilities: 295000,
          shareholdersEquity: {
            commonStock: 100000,
            additionalPaidInCapital: 75000,
            retainedEarnings: 100000,
            lessTreasuryStock: -5000,
            total: 270000,
          },
          totalLiabilitiesAndEquity: 565000,
        },
      };
    },

    objectToRows(obj, excludeKeys = ["total"]) {
      const rows = [];
      if (!obj || typeof obj !== "object") return rows;
      for (const [key, val] of Object.entries(obj)) {
        if (excludeKeys.includes(key)) continue;
        if (typeof val === "object" && val !== null && !Array.isArray(val))
          continue;
        const label =
          this.labelMap[key] ||
          key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase())
            .trim();
        rows.push({
          label,
          amount: Number(val) || 0,
          indent: true,
          isSubtotal: false,
          isSection: false,
        });
      }
      return rows;
    },

    buildAssetsRows() {
      const assets = this.rawData?.assets;
      if (!assets) return [];

      const rows = [];
      // Current Assets
      const ca = assets.currentAssets;
      if (ca) {
        rows.push({
          label: "Current Assets",
          amount: null,
          indent: false,
          isSubtotal: false,
          isSection: true,
        });
        rows.push(...this.objectToRows(ca));
        rows.push({
          label: "Total Current Assets",
          amount: ca.total ?? 0,
          indent: false,
          isSubtotal: true,
          isSection: false,
        });
      }
      // Non-Current Assets
      const nca = assets.nonCurrentAssets;
      if (nca) {
        rows.push({
          label: "Non-Current Assets",
          amount: null,
          indent: false,
          isSubtotal: false,
          isSection: true,
        });
        rows.push(...this.objectToRows(nca));
        rows.push({
          label: "Total Non-Current Assets",
          amount: nca.total ?? 0,
          indent: false,
          isSubtotal: true,
          isSection: false,
        });
      }
      return rows;
    },

    buildLiabilitiesEquityRows() {
      const le = this.rawData?.liabilitiesAndEquity;
      if (!le) return [];

      const rows = [];
      // Current Liabilities
      const cl = le.currentLiabilities;
      if (cl) {
        rows.push({
          label: "Current Liabilities",
          amount: null,
          indent: false,
          isSubtotal: false,
          isSection: true,
        });
        rows.push(...this.objectToRows(cl));
        rows.push({
          label: "Total Current Liabilities",
          amount: cl.total ?? 0,
          indent: false,
          isSubtotal: true,
          isSection: false,
        });
      }
      // Long Term Liabilities
      const ltl = le.longTermLiabilities;
      if (ltl) {
        rows.push({
          label: "Long Term Liabilities",
          amount: null,
          indent: false,
          isSubtotal: false,
          isSection: true,
        });
        rows.push(...this.objectToRows(ltl));
        rows.push({
          label: "Total Long Term Liabilities",
          amount: ltl.total ?? 0,
          indent: false,
          isSubtotal: true,
          isSection: false,
        });
      }
      rows.push({
        label: "Total Liabilities",
        amount: le.totalLiabilities ?? 0,
        indent: false,
        isSubtotal: true,
        isSection: false,
      });
      // Shareholders Equity
      const se = le.shareholdersEquity;
      if (se) {
        rows.push({
          label: "Shareholders Equity",
          amount: null,
          indent: false,
          isSubtotal: false,
          isSection: true,
        });
        rows.push(...this.objectToRows(se));
        rows.push({
          label: "Total Shareholders Equity",
          amount: se.total ?? 0,
          indent: false,
          isSubtotal: true,
          isSection: false,
        });
      }
      return rows;
    },

    formatCurrency(value) {
      if (value === undefined || value === null) return "-";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    print() {
      window.print();
    },

    close() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  font-weight: 500;
}

.balance-sheet-report {
  background: #f0f0f0;
  min-height: 100vh;
  padding: 15px;
}

.control-bar {
  background: white;
  padding: 12px 20px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    color: #000;
    font-weight: 600;
  }
}

.report-page {
  background: white;
  padding: 20px;
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 1100px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  border-bottom: 2px solid #000;
  padding-bottom: 12px;
  margin-bottom: 15px;

  .school-name {
    font-size: 1.4rem;
    font-weight: 700;
    color: #000;
    margin-bottom: 5px;
  }

  .report-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .header-meta {
    font-size: 0.9rem;
    color: #000;
    font-weight: 500;

    .separator {
      margin: 0 10px;
      color: #999;
    }
  }
}

.balance-sheet-layout {
  display: flex;
  gap: 40px;
  margin-bottom: 20px;
  min-height: 200px;
}

.bs-column {
  flex: 1;
  min-width: 0;
}

.bs-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 10px;
  padding-bottom: 4px;
  border-bottom: 2px solid #333;
}

.bs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  td {
    padding: 6px 10px;
    border-bottom: 1px solid #eee;
    color: #333;
  }

  .bs-label {
    text-align: left;
  }

  .bs-amount {
    white-space: nowrap;
    min-width: 100px;
  }

  .bs-total-row .bs-label,
  .bs-total-row .bs-amount {
    font-weight: 700;
    color: #000;
  }

  .bs-section-header .bs-label {
    font-weight: 600;
    color: #000;
  }

  .bs-indent {
    padding-left: 20px !important;
  }
}

.balance-status-banner {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  margin-bottom: 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;

  &.balanced {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  &.unbalanced {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
}

.bs-total-block {
  display: flex;
  justify-content: space-between;
  padding: 10px 10px 0;
  margin-top: 8px;
  border-top: 2px solid #000;
  font-size: 1rem;
}

.page-footer {
  text-align: center;
  padding-top: 12px;
  margin-top: 15px;
  border-top: 1px solid #ccc;
  color: #666;
}

.text-right {
  text-align: right;
}

@media print {
  .no-print {
    display: none !important;
  }

  .balance-sheet-report {
    background: white;
    padding: 0;
    min-height: auto;
  }

  .report-page {
    box-shadow: none;
    border: none;
    padding: 0;
    margin: 0;
    max-width: 100%;
  }

  .balance-sheet-layout {
    gap: 60px;
  }

  .bs-table td {
    border: 1px solid #ccc;
  }
}

@media (max-width: 768px) {
  .balance-sheet-layout {
    flex-direction: column;
  }
}
</style>
